"use client"

import { useRef, useState } from "react"
// Google recommends PlaceAutocompleteElement for new use; Autocomplete is still supported with bug fixes.
// Migration: https://developers.google.com/maps/documentation/javascript/places-migration-overview
import { Autocomplete } from "@react-google-maps/api"
import { Controller, useFormContext, Control, FieldValues, ControllerRenderProps, useWatch } from "react-hook-form"
import { Input } from "../ui/new-input"
import FormTextInputWrapper from "./form-input-wrapper"
import { MapPinDrop } from "./map-pin-drop"
import { Button } from "../ui/button"

type LocationDetails = {
  city: string
  state: string
  country: string
  latitude?: number
  longitude?: number
  zipCode?: string
}

interface StateCountryAutocompleteProps {
  setLocationDetails: (location: LocationDetails | null) => void
  locationDetails: LocationDetails | null
  control: Control<any>
  id?: any
  /** When true, show yellow background when value is empty (e.g. required on Profile) */
  highlightWhenEmpty?: boolean
  /** Optional label override (e.g. "Address (including city, state and zip)" without asterisk when not required) */
  label?: string
  /** Optional field name override (e.g. "volunteers.0.preferredLocation" for nested forms) */
  fieldName?: string
}

function toLatLngLiteral(
  loc: google.maps.LatLng | google.maps.LatLngLiteral
): google.maps.LatLngLiteral {
  if (typeof (loc as google.maps.LatLng).lat === "function") {
    const ll = loc as google.maps.LatLng
    return { lat: ll.lat(), lng: ll.lng() }
  }
  return loc as google.maps.LatLngLiteral
}

export const StateCountryAutocomplete: React.FC<
  StateCountryAutocompleteProps
> = ({ setLocationDetails, locationDetails, control, id, highlightWhenEmpty, label: labelProp, fieldName: fieldNameOverride }) => {
  const { setValue, clearErrors } = useFormContext()
  const [error, setError] = useState<string>("")
  const [showMapPinDrop, setShowMapPinDrop] = useState(false)
  const acRef = useRef<google.maps.places.Autocomplete | null>(null)

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    acRef.current = autocomplete
  }

  const handlePlaceChanged = (onChange: (val: string) => void) => {
    if (!acRef.current) return
    const place = acRef.current.getPlace()
    if (!place.formatted_address) return
    onChange(place.formatted_address)

    const components = place.address_components
    if (!place || !components || !place.geometry?.location) return

    let city = ""
    let stateShortName = ""
    let countryShortName = ""
    let zipCode = ""

    components.forEach((component) => {
      const types = component.types
      // Try multiple city types - some addresses use different types
      if (!city) {
        if (types.includes("locality")) {
          city = component.long_name
        } else if (types.includes("sublocality")) {
          city = component.long_name
        } else if (types.includes("sublocality_level_1")) {
          city = component.long_name
        } else if (types.includes("neighborhood")) {
          city = component.long_name
        } else if (types.includes("postal_town")) {
          city = component.long_name
        }
      }
      if (types.includes("administrative_area_level_1"))
        stateShortName = component.long_name
      if (types.includes("country")) countryShortName = component.long_name
      if (types.includes("postal_code")) zipCode = component.long_name
    })

    const { lat, lng } = toLatLngLiteral(place.geometry.location)

    // If city is still empty, try to extract from formatted_address
    if (!city && place.formatted_address) {
      // Try to extract city from formatted address (usually between street and state)
      const addressParts = place.formatted_address.split(",").map(s => s.trim())
      // City is typically the second-to-last part before state
      if (addressParts.length >= 2) {
        city = addressParts[addressParts.length - 2]
      }
    }

    // State and country are required - city should be set but we'll use a fallback if needed
    if (!stateShortName || !countryShortName) {
      setError("Please select a valid address that includes both state and country.")
      setLocationDetails({
        city: "",
        state: "",
        country: "",
        latitude: undefined,
        longitude: undefined,
        zipCode: "",
      })
      // Clear form values
      setValue("home.city", "", { shouldDirty: true })
      setValue("home.state", "", { shouldDirty: true })
      setValue("home.country", "", { shouldDirty: true })
      setValue("home.zipCode", "", { shouldDirty: true })
      return
    }

    // If city is still empty after all attempts, use a default or extract from address
    if (!city) {
      // Use the first part of the formatted address as a fallback
      if (place.formatted_address) {
        const parts = place.formatted_address.split(",")
        city = parts[0]?.trim() || "Unknown"
      } else {
        city = "Unknown"
      }
    }

    setError("")
    const newLocation: LocationDetails = {
      city,
      state: stateShortName,
      country: countryShortName,
      latitude: lat,
      longitude: lng,
      zipCode,
    }
    setLocationDetails(newLocation)
    setValue(controllerName, place.formatted_address, { shouldDirty: true })
    if (!fieldNameOverride) {
      setValue("home.city", city, { shouldDirty: true })
      setValue("home.state", stateShortName, { shouldDirty: true })
      setValue("home.country", countryShortName, { shouldDirty: true })
      setValue("home.zipCode", zipCode, { shouldDirty: true })
      setValue("home.latitude", lat, { shouldDirty: true })
      setValue("home.longitude", lng, { shouldDirty: true })
    }
  }

  const handleMapLocationSelected = (location: LocationDetails) => {
    // MapPinDrop has already set city/state/country/zip/lat/lng and home.address (user description).
    // This runs only after the user presses "Use This Location" in the map dialog.
    setLocationDetails(location)
    setShowMapPinDrop(false)
    setError("")
  }

  // Handle both string "1" and number 1 for id comparison
  const isProfileForm = id === 1 || id === "1"
  const controllerName = fieldNameOverride ?? (isProfileForm ? "preferredLocation" : "home.address")

  const openMapPinDrop = () => {
    clearErrors(controllerName as any)
    setError("")
    setShowMapPinDrop(true)
  }
  // Always call useWatch unconditionally (React Hook rules)
  const watchedPreferredLocation = useWatch({ control, name: controllerName })
  const watchedValue = (isProfileForm || fieldNameOverride) ? watchedPreferredLocation : undefined

  // Show map pin drop if enabled
  if (showMapPinDrop) {
    return (
      <div className="w-full">
        <MapPinDrop
          onLocationSelected={handleMapLocationSelected}
          onClose={() => setShowMapPinDrop(false)}
          control={control}
        />
      </div>
    )
  }

  return (
    <div className="w-full">
      <Controller
        name={controllerName}
        control={control}
        render={({ field, fieldState }: { field: ControllerRenderProps<FieldValues, any>; fieldState: { error?: { message?: string } } }) => {
          // Use watched value if field value is empty but watched value exists
          const displayValue = field.value || watchedValue || ""
          const validationError = fieldState?.error?.message
          const hasError = !!validationError || !!error
          const showEmptyHighlight = highlightWhenEmpty && !displayValue?.trim()
          
          return (
            <Autocomplete
              onLoad={handleLoad}
              onPlaceChanged={() => handlePlaceChanged(field.onChange)}
              options={{ types: ["address"] }}
            >
              <FormTextInputWrapper
                inputGroupProps={{
                  label: labelProp ?? (
                    <>
                      Address (including city, state and zip) <span className="whitespace-nowrap">*</span>
                    </>
                  ),
                  error: validationError || error || undefined,
                }}
              >
                <Input
                  placeholder="Address (including city, state and zip)"
                  value={displayValue}
                  variant={hasError ? "destructive" : "default"}
                  className={hasError ? "border-destructive" : showEmptyHighlight ? "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20" : undefined}
                  style={showEmptyHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : undefined}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    // If address is cleared, also clear city and state
                    if (!e.target.value || e.target.value.trim() === "") {
                      setValue("home.city", "", { shouldDirty: true })
                      setValue("home.state", "", { shouldDirty: true })
                      setValue("home.country", "", { shouldDirty: true })
                      setValue("home.zipCode", "", { shouldDirty: true })
                      setValue("home.latitude", undefined, { shouldDirty: true })
                      setValue("home.longitude", undefined, { shouldDirty: true })
                      setLocationDetails(null)
                    }
                  }}
                />
              </FormTextInputWrapper>
            </Autocomplete>
          )
        }}
      />

      {/* "I don't have a street address" link */}
      <div className="mt-2">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm text-blue-600 hover:text-blue-700 hover:underline"
          onClick={openMapPinDrop}
          showLoader={false}
        >
          I don&apos;t have a street address
        </Button>
      </div>

    </div>
  )
}
